import { useEffect, useState } from "react";
import type {
  MouseEvent,
  PointerEvent,
  ReactNode,
  SyntheticEvent,
} from "react";
import Button from "../../../shared/components/Button";
import { settingsFields, type WorkflowNode } from "../model";
import { nodeHeight, nodeWidth } from "../node-geometry";
import { AudioSpectrum } from "./AudioSpectrum";
import { getWorkflowDepth, getWorkflowNodeMetrics, type WorkflowDepthData, type WorkflowNodeMetrics } from "../api/workflow-api";
import "./Node.scss";
import { NodeGlyph, WarningGlyph } from "./NodeVisuals";
import { kindColor, nodePortTypes, portLabel, portType } from "./nodeStyles";

const GRID_SIZE = 24;
const NODE_HEIGHT = GRID_SIZE * 6;

function fitToGrid(value: number) {
  return Math.ceil(value / GRID_SIZE) * GRID_SIZE;
}

type NodeProps = {
  node: WorkflowNode;
  selected: boolean;
  connectingFrom: { nodeID: string; portID: string } | null;
  running: boolean;
  runtimeStarting?: boolean;
  onDragStart: (event: PointerEvent, node: WorkflowNode) => void;
  onSelect: (nodeID: string, additive: boolean) => void;
  onOpenSettings: (nodeID: string) => void;
  onConnectionStart: (
    event: PointerEvent,
    nodeID: string,
    side: "input" | "output",
    portID?: string,
  ) => void;
  connectionDrag: {
    nodeID: string;
    side: "input" | "output";
    portID: string;
  } | null;
  onConnect: (
    nodeID: string,
    side: "input" | "output",
    portID?: string,
  ) => void;
  onContextMenu: (event: MouseEvent, node: WorkflowNode) => void;
  onWarningContextMenu: (event: MouseEvent, warning: string) => void;
  validationWarnings: string[];
  onAddOutput: (nodeID: string) => void;
  onOutputContextMenu: (
    event: MouseEvent,
    nodeID: string,
    portID: string,
  ) => void;
  hasInput: boolean;
  hasAudioInput: boolean;
  activityVersion: number;
  previewVersion: string;
  onFrame: (nodeID: string) => void;
};

type PortContextMenuHandler = (
  event: MouseEvent,
  side: "input" | "output",
  portID: string,
) => void;

function retryPreview(event: SyntheticEvent<HTMLImageElement>) {
  const image = event.currentTarget;
  image.hidden = true;
  image.nextElementSibling?.removeAttribute("hidden");
  const retries = Number(image.dataset.previewRetries ?? "0");
  if (retries >= 5) {
    return;
  }
  image.dataset.previewRetries = String(retries + 1);
  window.setTimeout(() => {
    const baseURL = image.src.split("&retry=")[0];
    image.hidden = false;
    image.src = `${baseURL}&retry=${Date.now()}`;
  }, 1000);
}

function previewLoaded(event: SyntheticEvent<HTMLImageElement>) {
  const image = event.currentTarget;
  image.hidden = false;
  image.nextElementSibling?.setAttribute("hidden", "true");
}

type CardProps = {
  node: WorkflowNode;
  children: ReactNode;
  selected: boolean;
  minHeight?: string;
  onSelect: (nodeID: string, additive: boolean) => void;
  onOpenSettings: (nodeID: string) => void;
  onContextMenu: (event: MouseEvent, node: WorkflowNode) => void;
};

function Card({
  node,
  children,
  selected,
  minHeight,
  onSelect,
  onOpenSettings,
  onContextMenu,
}: CardProps) {
  return (
    <article
      className={`node ${node.kind === "camera" ? "node--camera" : ""} ${node.kind === "display" ? "node--display" : ""} ${node.kind === "split" ? "node--split" : ""} ${node.kind === "group" ? "node--group" : ""} ${selected ? "selected" : ""}`}
      style={{
        left: node.x,
        top: node.y,
        minHeight: minHeight ?? `${nodeHeight(node)}px`,
        width: nodeWidth(node),
      }}
      onClick={(event) => onSelect(node.id, event.shiftKey)}
      onDoubleClick={() => onOpenSettings(node.id)}
      onContextMenu={(event) => onContextMenu(event, node)}
    >
      {children}
    </article>
  );
}

function Header({
  node,
  onDragStart,
  validationWarnings,
  onWarningContextMenu,
}: {
  node: WorkflowNode;
  onDragStart: (event: PointerEvent, node: WorkflowNode) => void;
  validationWarnings: string[];
  onWarningContextMenu: (event: MouseEvent, warning: string) => void;
}) {
  return (
    <div
      className="node-header"
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.stopPropagation();
        onDragStart(event, node);
      }}
    >
      <div className={`node-icon ${kindColor(node.category)}`}>
        <NodeGlyph category={node.category} />
      </div>
      <div className="node-copy">
        <p className="node-title">{node.title}</p>
		{node.capability === "planned" && <span className="node-capability">Planned</span>}
      </div>
      {validationWarnings.length > 0 && (
        <span
          aria-label={validationWarnings.join(". ")}
          className="node-warning"
          tabIndex={0}
          title={validationWarnings.join(". ")}
          onContextMenu={(event) =>
            onWarningContextMenu(event, validationWarnings.join(". "))
          }
        >
          <WarningGlyph />
          <span className="node-warning-tooltip" role="tooltip">
            {validationWarnings.join(". ")}
          </span>
        </span>
      )}
    </div>
  );
}

function InputSection({
  node,
  connectingFrom,
  isDraggingInput,
  onConnectionStart,
  onConnect,
  onPortContextMenu,
  contextPort,
  connectionDrag,
  activityVersion,
}: Pick<
  NodeProps,
  "node" | "connectingFrom" | "onConnectionStart" | "onConnect"
> & {
  isDraggingInput: boolean;
  contextPort: string | null;
  onPortContextMenu: PortContextMenuHandler;
  connectionDrag: NodeProps["connectionDrag"];
  activityVersion: number;
}) {
  if (node.input === "None") return null;
  const inputPorts = node.inputPorts ?? ["input"];
  return inputPorts.map((portID, index) => {
    const inputType = portType(portID === "input" ? node.input : portID);
    const isConnectionSource =
      connectionDrag?.nodeID === node.id &&
      connectionDrag.side === "input" &&
      connectionDrag.portID === portID;
    const isOtherPort = connectionDrag !== null && !isConnectionSource;
    return (
      <div
        className={`node-port-group node-port-group--input node-port-group--${inputType}`}
        key={portID}
        style={
          inputPorts.length > 1
            ? { top: `${50 + (index - (inputPorts.length - 1) / 2) * 18}%` }
            : undefined
        }
      >
        <span className="node-port-label">{portLabel(inputType)}</span>
        <Button
          aria-label={`Connect ${portID} input to ${node.title}`}
          className={`node-handle node-handle--input node-handle--${inputType} ${connectingFrom ? "node-handle--connecting" : "node-handle--ready"} ${isDraggingInput ? "node-handle--disconnecting" : ""} ${isConnectionSource ? "node-handle--connection-source" : ""} ${isOtherPort ? "node-handle--other-port" : ""} ${contextPort === `input:${portID}` ? "node-handle--context-selected" : ""} ${activityVersion ? `node-handle--pulse-${activityVersion % 2}` : ""}`}
          data-node-id={node.id}
          data-port-id={portID}
          data-port-side="input"
          onPointerDown={(event) => {
            if (event.button === 1) return;
            event.stopPropagation();
            onConnectionStart(event, node.id, "input", portID);
          }}
          onClick={(event) => {
            event.stopPropagation();
            onConnect(node.id, "input", portID);
          }}
          onContextMenu={(event) => onPortContextMenu(event, "input", portID)}
        />
      </div>
    );
  });
}

function OutputSection({
  node,
  outputPorts,
  outputType,
  isDraggingOutput,
  splitHeight,
  onConnectionStart,
  onConnect,
  onPortContextMenu,
  contextPort,
  connectionDrag,
  onOutputContextMenu,
  activityVersion,
}: Pick<NodeProps, "node" | "onConnectionStart" | "onConnect"> & {
  outputPorts: string[];
  outputType: ReturnType<typeof portType>;
  isDraggingOutput: boolean;
  splitHeight: number;
  contextPort: string | null;
  onPortContextMenu: PortContextMenuHandler;
  connectionDrag: NodeProps["connectionDrag"];
  onOutputContextMenu: NodeProps["onOutputContextMenu"];
  activityVersion: number;
}) {
  return outputPorts.map((portID, index) => {
    const portOutputType = nodePortTypes(node, "output", portID)[0] ?? outputType;
    const isConnectionSource =
      connectionDrag?.nodeID === node.id &&
      connectionDrag.side === "output" &&
      connectionDrag.portID === portID;
    const isOtherPort = connectionDrag !== null && !isConnectionSource;
    return (
      <div
        className={`node-port-group node-port-group--output node-port-group--${portOutputType}`}
        key={portID}
        style={
          outputPorts.length > 1
            ? {
                top: `${64 + (index / (outputPorts.length - 1)) * (splitHeight - 100)}px`,
              }
            : undefined
        }
      >
        <Button
          aria-label={`Connect ${portID} from ${node.title}`}
          className={`node-handle node-handle--output node-handle--${portOutputType} ${isDraggingOutput ? "node-handle--disconnecting" : ""} ${isConnectionSource ? "node-handle--connection-source" : ""} ${isOtherPort ? "node-handle--other-port" : ""} ${contextPort === `output:${portID}` ? "node-handle--context-selected" : ""} ${activityVersion ? `node-handle--pulse-${activityVersion % 2}` : ""}`}
          data-node-id={node.id}
          data-port-id={portID}
          data-port-side="output"
          onPointerDown={(event) => {
            if (event.button === 1) return;
            event.stopPropagation();
            onConnectionStart(event, node.id, "output", portID);
          }}
          onClick={(event) => {
            event.stopPropagation();
            onConnect(node.id, "output", portID);
          }}
          onContextMenu={(event) => {
            onPortContextMenu(event, "output", portID);
            if (node.kind === "split")
              onOutputContextMenu(event, node.id, portID);
          }}
        />
        <span className="node-port-label">
          {portLabelFor(node, portID, outputType)}
        </span>
      </div>
    );
  });
}

function portLabelFor(
  node: WorkflowNode,
  portID: string,
  outputType: ReturnType<typeof portType>,
) {
  return (
    node.outputPorts?.find((port) => port.id === portID)?.label ??
    (node.kind === "split"
      ? portID.replace("branch-", "Branch ")
      : node.kind === "mediaSplit"
        ? portID.toUpperCase()
        : node.kind === "group"
          ? portID.replace("group-output-", "Output ")
          : portLabel(outputType))
  );
}

function Body({
  node,
  hasInput,
  hasAudioInput,
  activityVersion,
  displayFPS,
  onAddOutput,
  running,
  onSourceFrameError,
  onSourceFrameLoad,
  onDisplayFrameLoad,
  previewVersion,
}: Pick<
  NodeProps,
  "node" | "hasInput" | "onAddOutput" | "running" | "activityVersion"
> & {
  previewVersion: string;
  displayFPS: number;
  hasAudioInput: boolean;
  onSourceFrameError: (event: SyntheticEvent<HTMLImageElement>) => void;
  onSourceFrameLoad: (event: SyntheticEvent<HTMLImageElement>) => void;
  onDisplayFrameLoad: (event: SyntheticEvent<HTMLImageElement>) => void;
}) {
  const [metrics, setMetrics] = useState<WorkflowNodeMetrics | null>(null);
  useEffect(() => {
    if (!running || node.kind !== "background") return;
    let active = true;
    const load = () => getWorkflowNodeMetrics(node.id).then((value) => active && setMetrics(value)).catch(() => undefined);
    void load();
    const timer = window.setInterval(load, 1000);
    return () => { active = false; window.clearInterval(timer); };
  }, [node.id, node.kind, running]);
  return (
    <div className="node-content">
      {node.kind === "camera" && (
        <div
          className={`node-preview${node.config.source ? "" : " node-preview--empty"}`}
        >
          {node.config.source && running ? (
            <>
              <img
                onError={onSourceFrameError}
                onLoad={onSourceFrameLoad}
                src={`/api/workflow/runtime/outputs/${encodeURIComponent(node.id)}/stream?fps=1&runtime=${running ? "running" : "stopped"}&graph=${encodeURIComponent(previewVersion)}`}
                alt="Live camera frame"
              />
              <span className="node-preview-status" hidden>
                Not Operational
              </span>
            </>
          ) : (
            <span className="node-preview-status">
              {node.config.source
                ? "Not Operational"
                : "Configure camera source"}
            </span>
          )}
        </div>
      )}
      {node.kind === "display" && (
        <div className="node-display-previews">
          <div
            className={`node-preview ${hasInput ? "" : "node-preview--empty"}`}
          >
            {hasInput && running ? (
              <>
                <img
                  onError={retryPreview}
                  onLoad={onDisplayFrameLoad}
                  src={`/api/workflow/runtime/outputs/${encodeURIComponent(node.id)}/stream?fps=${displayFPS}&runtime=${running ? "running" : "stopped"}&graph=${encodeURIComponent(previewVersion)}`}
                  alt="Connected frame preview"
                />
                <span className="node-preview-status" hidden>
                  Not Operational
                </span>
              </>
            ) : (
              <span className="node-preview-status">
                {hasInput ? "Not Operational" : "Connect a video input"}
              </span>
            )}
          </div>
          {hasAudioInput && <AudioSpectrum nodeID={node.id} />}
        </div>
      )}
      {isDepthDataNode(node.kind) && (
        <DepthDataView
          node={node}
          running={running}
          hasInput={hasInput}
          previewVersion={previewVersion}
        />
      )}
      <div className="node-body">
        <NodeDataRows
          active={Boolean(activityVersion)}
          hasInput={hasInput}
          node={node}
          properties={Object.entries(node.properties).slice(0, 2)}
          sequence={activityVersion}
          metrics={metrics}
        />
        {node.kind === "split" && (
          <Button
            className="node-add-output"
            fullWidth
            onClick={(event) => {
              event.stopPropagation();
              onAddOutput(node.id);
            }}
            onMouseDown={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
            onPointerUp={(event) => event.stopPropagation()}
            variant="primary"
          >
            Add output
          </Button>
        )}
        {node.kind === "group" && (
          <div className="node-group-summary">
            <span>{node.group?.nodes.length ?? 0} inner nodes</span>
            <span>
              {node.group?.inputs.length ?? 0} in ·{" "}
              {node.group?.outputs.length ?? 0} out
            </span>
          </div>
        )}
        {node.kind === "mediaSplit" && (
          <div className="node-group-summary">
            <span>Video</span>
            <span>Audio</span>
          </div>
        )}
      </div>
    </div>
  );
}

function isDepthDataNode(kind: WorkflowNode["kind"]) {
  return ["depthMap", "depthProbe", "rangeDisplay", "metricCard", "depthZone"].includes(kind);
}

function DepthDataView({
  node,
  running,
  hasInput,
  previewVersion,
}: {
  node: WorkflowNode;
  running: boolean;
  hasInput: boolean;
  previewVersion: string;
}) {
  const [depth, setDepth] = useState<WorkflowDepthData | null>(null);
  useEffect(() => {
    if (!running || !hasInput) {
      return;
    }
    let active = true;
    const load = () => getWorkflowDepth(node.id).then((data) => active && setDepth(data)).catch(() => active && setDepth(null));
    void load();
    const interval = window.setInterval(load, 1000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [hasInput, node.id, previewVersion, running]);
  if (!running || !hasInput) return <div className="node-depth-empty">{hasInput ? "Not Operational" : "Connect depth input"}</div>;
  if (node.kind === "depthMap") return <div className="node-preview"><img src={`/api/workflow/runtime/outputs/${encodeURIComponent(node.id)}/stream?fps=1&graph=${encodeURIComponent(previewVersion)}`} onError={retryPreview} onLoad={previewLoaded} alt="Depth map" /><span className="node-preview-status" hidden>Not Operational</span></div>;
  const units = node.config.units === "Meters" ? "m" : "rel";
  if (!depth) return <div className="node-depth-empty">Waiting for depth data</div>;
  if (node.kind === "depthProbe") return <DepthRows rows={[["Center", depth.mean], ["Near", depth.min], ["Far", depth.max]]} units={units} />;
  if (node.kind === "depthZone") return <DepthRows rows={[["Mean", depth.mean], ["Limit", Number(node.config.maximumRange ?? 5)]]} units={units} />;
  const value = node.kind === "metricCard" ? depth[(node.config.value ?? "Mean").toLowerCase() as "min" | "mean" | "max"] : depth.mean;
  return <div className="node-metric"><small>{node.kind === "metricCard" ? node.config.label || "Metric" : "Average range"}</small><strong>{value.toFixed(2)} <em>{units}</em></strong>{node.kind === "rangeDisplay" && <DepthRows rows={[["Near", depth.min], ["Far", depth.max]]} units={units} />}</div>;
}

function DepthRows({ rows, units }: { rows: [string, number][]; units: string }) {
  return <div className="node-depth-rows">{rows.map(([label, value]) => <span key={label}><b>{label}</b><strong>{value.toFixed(2)} {units}</strong></span>)}</div>;
}

function NodeDataRows({
  node,
  hasInput,
  active,
  sequence,
  properties,
  metrics,
}: {
  node: WorkflowNode;
  hasInput: boolean;
  active: boolean;
  sequence: number;
  properties: [string, string][];
  metrics: WorkflowNodeMetrics | null;
}) {
  if (node.kind === "background") {
    return <div className="node-data-rows" aria-label="Background mask metrics">
      <span className="node-data-row node-data-row--config"><b>ALGORITHM</b><span>{node.config.algorithm || "Adaptive background"}</span></span>
      <span className="node-data-row node-data-row--config"><b>THRESHOLD</b><span>{node.config.threshold || "16"}</span></span>
      <span className="node-data-row node-data-row--config"><b>LEARNING</b><span>{node.config.learningRate || "0.01"}</span></span>
      <span className="node-data-row"><b>FOREGROUND</b><span>{metrics ? metrics.foreground_pixels.toLocaleString() : "—"}</span><em>pixels</em></span>
      <span className="node-data-row"><b>BACKGROUND</b><span>{metrics ? metrics.background_pixels.toLocaleString() : "—"}</span><em>pixels</em></span>
      <span className="node-data-row"><b>COVERAGE</b><span>{metrics ? `${(metrics.foreground_ratio * 100).toFixed(2)}%` : "—"}</span><em>motion</em></span>
    </div>;
  }
  const configured = (settingsFields[node.kind] ?? [])
    .map((field) => [
      compactPropertyLabel(field.label, field.key),
      node.config[field.key] || field.defaultValue || "",
    ] as [string, string])
    .filter(([, value]) => value)
    .slice(0, 3);
  const details = configured.length > 0
    ? configured
    : properties
        .map(([label, value]) => [compactPropertyLabel(label, label), value] as [string, string])
        .slice(0, 3);
  return (
    <div className="node-data-rows" aria-label="Node details">
      {details.map(([label, value]) => (
        <span className="node-data-row node-data-row--config" key={label}>
          <b>{label}</b>
          <span>{value}</span>
        </span>
      ))}
      {active && (
        <span className="node-data-row">
          <b>UPDATED</b>
          <span>#{sequence}</span>
          <em>live</em>
        </span>
      )}
      {!active && node.input !== "None" && !hasInput && (
        <span className="node-data-row">
          <b>STATUS</b>
          <span>Connect input</span>
        </span>
      )}
    </div>
  );
}

function compactPropertyLabel(label: string, key: string) {
  const labels: Record<string, string> = {
    apiKey: 'Key',
    cacheBust: 'Cache',
    clockRate: 'Clock',
    contentType: 'Type',
    endpoint: 'Source',
    fps: 'FPS',
    foreground: 'Foreground',
    foregroundMask: 'Foreground',
    iceServers: 'ICE',
    includeState: 'State',
    jitterBuffer: 'Jitter',
    learningRate: 'Learning',
    maxQueue: 'Queue',
    maxWidth: 'Width',
    onMotion: 'Motion',
    pullInterval: 'Interval',
    resolution: 'Resolution',
    segmentWindow: 'Window',
    source: 'Source',
    startAt: 'Start',
    streamID: 'Stream',
    timeout: 'Timeout',
    username: 'User',
    url: 'Source',
  };
  const normalizedKey = key.replace(/[-_ ]([a-z])/g, (_, character: string) => character.toUpperCase());
  if (labels[normalizedKey]) return labels[normalizedKey];
  const shortLabel = label.replace(/\s*\([^)]*\)/g, '').trim().split(/\s+/).pop();
  return shortLabel ? shortLabel[0].toUpperCase() + shortLabel.slice(1) : label;
}

function Footer({
  node,
  running,
  runtimeStarting,
  sourceFrameReady,
  activityVersion,
  hasInput,
}: Pick<NodeProps, "node" | "running" | "runtimeStarting" | "hasInput"> & {
  sourceFrameReady: boolean;
  activityVersion: number;
}) {
  const state = !running
    ? "stopped"
    : runtimeStarting ||
        (node.category === "source" &&
          node.kind === "camera" &&
          !sourceFrameReady)
      ? "waiting"
      : node.category === "source"
        ? "streaming"
        : hasInput
          ? "receiving"
          : "running";

  return (
    <footer className="node-footer">
      <div className="node-status">
        <span
          className={`node-status-dot node-status-dot--${state}${activityVersion ? ` node-status-dot--pulse-${activityVersion % 2}` : ""}`}
        />
        <span className="node-status-label">
          {state === "stopped"
            ? "Stopped"
            : state === "waiting"
              ? "Waiting"
              : state === "receiving"
                ? "Receiving"
                : running
                  ? node.status
                  : "Paused"}
        </span>
      </div>
    </footer>
  );
}

export type ContextMenuItem = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  disabledReason?: string;
  variant?: "ghost" | "danger";
};

export function ContextMenu({
  items,
  x,
  y,
}: {
  items: ContextMenuItem[];
  x: number;
  y: number;
}) {
  return (
    <div
      className="context-menu"
      style={{ left: x, top: y }}
      onClick={(event) => event.stopPropagation()}
    >
      {items.map((item) => {
        const button = (
          <Button
            className={`context-action${item.variant === "danger" ? " context-action--danger" : ""}`}
            disabled={item.disabled}
            onClick={item.onClick}
            variant={item.variant ?? "ghost"}
          >
            {item.label}
          </Button>
        );
        return item.disabled && item.disabledReason ? (
          <span
            className="context-action-wrapper"
            key={item.label}
            title={item.disabledReason}
          >
            {button}
          </span>
        ) : (
          <span className="context-action-wrapper" key={item.label}>
            {button}
          </span>
        );
      })}
    </div>
  );
}

export function Node({
  node,
  selected,
  connectingFrom,
  running,
  runtimeStarting = false,
  onDragStart,
  onSelect,
  onOpenSettings,
  onConnectionStart,
  connectionDrag,
  onConnect,
  onContextMenu,
  onWarningContextMenu,
  validationWarnings,
  onAddOutput,
  onOutputContextMenu,
  hasInput,
  hasAudioInput,
  activityVersion,
  previewVersion,
  onFrame,
}: NodeProps) {
  const sourceFrameKey = `${node.id}:${node.config.source ?? ""}:${running}`;
  const [sourceFrameReadyKey, setSourceFrameReadyKey] = useState("");
  const [contextPort, setContextPort] = useState<string | null>(null);
  const sourceFrameReady = sourceFrameReadyKey === sourceFrameKey;

  useEffect(() => {
    if (!contextPort) return;
    const clearContextPort = (event: globalThis.PointerEvent) => {
      if (
        event.target instanceof Element &&
        event.target.closest(".node-handle")
      ) {
        return;
      }
      setContextPort(null);
    };
    document.addEventListener("pointerdown", clearContextPort);
    return () => document.removeEventListener("pointerdown", clearContextPort);
  }, [contextPort]);
  const handlePortContextMenu = (
    event: MouseEvent,
    side: "input" | "output",
    portID: string,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setContextPort(`${side}:${portID}`);
  };

  const handleSourceFrameError = (event: SyntheticEvent<HTMLImageElement>) => {
    setSourceFrameReadyKey("");
    retryPreview(event);
  };

  const handleSourceFrameLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    setSourceFrameReadyKey(sourceFrameKey);
    previewLoaded(event);
    onFrame(node.id);
  };
  const handleDisplayFrameLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    previewLoaded(event);
    onFrame(node.id);
  };
  const isDraggingOutput =
    connectionDrag?.nodeID === node.id && connectionDrag.side === "output";
  const isDraggingInput =
    connectionDrag?.nodeID === node.id && connectionDrag.side === "input";
  const displayFPS = Math.min(
    60,
    Math.max(1, Number(node.config.fps ?? "1") || 1),
  );
  const outputType = portType(node.output);
  const outputCount = node.outputPorts?.length
    ? node.outputPorts.length
    : node.kind === "mediaSplit"
      ? 2
      : node.group
        ? Math.max(1, node.group.outputs.length)
        : Math.max(2, Number(node.config.outputs ?? "2") || 2);
  const splitHeight = Math.max(NODE_HEIGHT, fitToGrid(120 + outputCount * 36));
  const outputPorts = node.outputPorts?.map((port) => port.id) ??
    (node.kind === "split"
      ? Array.from({ length: outputCount }, (_, index) => `branch-${index + 1}`)
      : node.kind === "mediaSplit"
        ? ["video", "audio"]
        : node.kind === "group"
          ? Array.from(
              { length: outputCount },
              (_, index) => `group-output-${index + 1}`,
            )
          : ["output"]);
  const multiInputHeight = node.inputPorts?.length
    ? `${Math.max(NODE_HEIGHT, fitToGrid(120 + node.inputPorts.length * 24))}px`
    : undefined;

  return (
    <Card
      minHeight={
        outputPorts.length > 1 ||
        node.kind === "split" ||
        node.kind === "mediaSplit" ||
        node.kind === "group"
          ? `${splitHeight}px`
          : multiInputHeight
      }
      node={node}
      onContextMenu={onContextMenu}
      onOpenSettings={onOpenSettings}
      onSelect={onSelect}
      selected={selected}
    >
      <InputSection
        connectingFrom={connectingFrom}
        isDraggingInput={isDraggingInput}
        node={node}
        onConnect={onConnect}
        onConnectionStart={onConnectionStart}
        onPortContextMenu={handlePortContextMenu}
        contextPort={contextPort}
        connectionDrag={connectionDrag}
        activityVersion={activityVersion}
      />
      <OutputSection
        isDraggingOutput={isDraggingOutput}
        node={node}
        onConnect={onConnect}
        onConnectionStart={onConnectionStart}
        onPortContextMenu={handlePortContextMenu}
        contextPort={contextPort}
        connectionDrag={connectionDrag}
        activityVersion={activityVersion}
        onOutputContextMenu={onOutputContextMenu}
        outputPorts={outputPorts}
        outputType={outputType}
        splitHeight={splitHeight}
      />
      <Header
        node={node}
        onDragStart={onDragStart}
        validationWarnings={validationWarnings}
        onWarningContextMenu={onWarningContextMenu}
      />
      <Body
        activityVersion={activityVersion}
        displayFPS={displayFPS}
        hasInput={hasInput}
        hasAudioInput={hasAudioInput}
        node={node}
        onAddOutput={onAddOutput}
        onSourceFrameError={handleSourceFrameError}
        onSourceFrameLoad={handleSourceFrameLoad}
        previewVersion={previewVersion}
        onDisplayFrameLoad={handleDisplayFrameLoad}
        running={running}
      />
      <Footer
        hasInput={hasInput}
        node={node}
        running={running}
        runtimeStarting={runtimeStarting}
        sourceFrameReady={sourceFrameReady}
        activityVersion={activityVersion}
      />
    </Card>
  );
}
